import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { easeOutExpo, LOGO_FIXED, NAV_LINKS } from "./shared";

/**
 * Fixed header — logo (left desktop / center mobile) + nav (right desktop).
 * Logo opacity progressively decreases on scroll; fully invisible by the
 * closing CTA / footer. Visible "M" of the PNG aligns with H1's "D" via
 * translateX(-30.7%) to offset the PNG's empty left padding.
 */
export const FixedHeader = ({ activeSection = "hero" }) => {
  const { scrollYProgress } = useScroll();

  // Mobile-only floating logo: visible on hero, fades out as the user scrolls
  // away. Desktop uses an inline logo inside HeroSection so the alignment
  // with the H1 is reliable across viewport heights.
  const logoOpacity = useTransform(
    scrollYProgress,
    [0, 0.04, 0.1, 0.14],
    [0.7, 0.65, 0.15, 0]
  );
  const navOpacity = useTransform(
    scrollYProgress,
    [0, 0.85, 0.95, 1],
    [1, 1, 0.5, 0]
  );

  const onAnchorClick = (e, href) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    const id = href.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <motion.header
        className="fixed top-0 inset-x-0 z-50 pointer-events-none"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.15 }}
        data-testid="fixed-header"
      >
        <div className="relative px-6 md:px-10 lg:px-14 pt-5 md:pt-6">
          <motion.nav
            style={{ opacity: navOpacity }}
            className="pointer-events-auto hidden md:flex items-center justify-end gap-9 lg:gap-12"
            data-testid="fixed-nav"
            aria-label="Primary"
          >
            {NAV_LINKS.map((link) => {
              const isActive = activeSection === link.href.slice(1);
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => onAnchorClick(e, link.href)}
                  data-testid={`nav-link-${link.href.slice(1)}`}
                  aria-current={isActive ? "page" : undefined}
                  className={`group relative text-[11px] lg:text-xs tracking-[0.24em] uppercase font-sans font-semibold transition-colors duration-300 ${
                    isActive ? "text-white" : "text-white/85 hover:text-white"
                  }`}
                  style={{ textShadow: "0 1px 14px rgba(0,0,0,0.55)" }}
                >
                  {link.label}
                  <span
                    aria-hidden="true"
                    className={`absolute -bottom-1.5 left-0 right-0 h-px bg-white origin-left transition-transform duration-500 ease-out ${
                      isActive
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </a>
              );
            })}
          </motion.nav>

          <motion.a
            href="#"
            onClick={(e) => onAnchorClick(e, "#")}
            aria-label="MatiMitra Biochar — back to top"
            style={{ opacity: logoOpacity }}
            className="pointer-events-auto md:hidden absolute left-1/2 top-2 -translate-x-1/2"
            data-testid="fixed-logo-mobile"
          >
            <img
              src={LOGO_FIXED}
              alt="MatiMitra Biochar"
              className="h-48 w-auto select-none"
              style={{
                filter:
                  "drop-shadow(0 2px 16px rgba(0,0,0,0.55)) drop-shadow(0 1px 3px rgba(0,0,0,0.45))",
              }}
              draggable="false"
            />
          </motion.a>
        </div>
      </motion.header>
    </>
  );
};
